import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StoreContext } from "shared/context/store-context";
import StockMovementItem from "./StockMovementItem";

import Layout from "layouts/LayoutMasterDetail";

const StockMovements = ({ stockId }) => {
  const { t } = useTranslation();
  const listName = "stock-movements";
  const itemType = "stock-movement";

  const {
    stockMovements,
    stocks,
    attendants,
    drivers,
    vehicles,
    thirdParties,
    InsertStockMovement,
    DeleteStockMovement,
    UpdateStockMovement,
  } = useContext(StoreContext);

  const [pageItems, setPageItems] = useState([]);

  // Fetch all stock movements on component mount
  useEffect(() => {
    if (stockId) {
      setPageItems(stockMovements.filter((movement) => movement.stock === stockId));
    } else {
      setPageItems(stockMovements);
    }
  }, [stockMovements, stockId]);

  const getMovementTypeLabel = (movementType) => {
    return movementType === "in" ? t("inventoryStockMovement.movementType.in") : t("inventoryStockMovement.movementType.out");
  };

  const getMovementTypeClass = (movementType) => {
    return movementType === "in" ? "movement-in" : "movement-out";
  };

  // Helper function to find stock by ID
  const findStockById = (stockId) => {
    return stocks.find((stock) => stock.id === stockId);
  };

  // Helper function to find third party (muhatap) by ID
  const findThirdPartyById = (thirdPartyId) => {
    if (!thirdPartyId) return null;
    return thirdParties?.find((partner) => partner.id === thirdPartyId) || null;
  };

  // Helper function to format cost values
  const formatCost = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const number = Number(value);
    if (Number.isNaN(number)) return "-";
    return number.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Helper function to find assignee by ID and type
  const findAssigneeById = (assigneeId, assigneeType) => {
    if (!assigneeId || !assigneeType) return null;

    if (assigneeType === "attendant") {
      return attendants?.find((attendant) => attendant.id === assigneeId);
    } else if (assigneeType === "driver") {
      return drivers?.find((driver) => driver.id === assigneeId);
    } else if (assigneeType === "vehicle") {
      return vehicles?.find((vehicle) => vehicle.id === assigneeId);
    }
    return null;
  };

  // Helper function to convert stock units to Turkish
  const getStockUnitsInTurkish = (unit) => {
    const unitCanonicalMap = {
      // Temel birimler
      piece: "piece",
      pieces: "piece",
      pcs: "piece",
      unit: "unit",
      units: "unit",

      // Ağırlık birimleri
      kg: "kg",
      kilogram: "kg",
      kilograms: "kg",
      g: "gram",
      gram: "gram",
      grams: "gram",
      ton: "ton",
      tons: "ton",
      lb: "lbs",
      lbs: "lbs",
      pound: "lbs",
      pounds: "lbs",

      // Hacim birimleri
      l: "liter",
      liter: "liter",
      liters: "liter",
      ml: "ml",
      milliliter: "ml",
      milliliters: "ml",
      m3: "m3",
      cubic_meter: "m3",
      cubic_meters: "m3",

      // Uzunluk birimleri
      m: "meter",
      meter: "meter",
      meters: "meter",
      cm: "cm",
      centimeter: "cm",
      centimeters: "cm",
      mm: "mm",
      millimeter: "mm",
      millimeters: "mm",
      km: "km",
      kilometer: "km",
      kilometers: "km",
      inch: "inch",
      inches: "inch",
      ft: "feet",
      feet: "feet",

      // Alan birimleri
      m2: "m2",
      square_meter: "m2",
      square_meters: "m2",
      cm2: "cm2",
      square_centimeter: "cm2",
      square_centimeters: "cm2",

      // Paket birimleri
      box: "box",
      boxes: "box",
      pack: "pack",
      packs: "pack",
      package: "pack",
      packages: "pack",
      bundle: "bundle",
      bundles: "bundle",
      roll: "roll",
      rolls: "roll",
      set: "set",
      sets: "set",
      pair: "pair",
      pairs: "pair",

      // Zaman birimleri
      hour: "hour",
      hours: "hour",
      day: "day",
      days: "day",
      week: "week",
      weeks: "week",
      month: "month",
      months: "month",
      year: "year",
      years: "year",

      // Diğer birimler
      bottle: "bottle",
      bottles: "bottle",
      can: "can",
      cans: "can",
      bag: "bag",
      bags: "bag",
      container: "container",
      containers: "container",
      pallet: "pallet",
      pallets: "pallet",
      carton: "carton",
      cartons: "carton",
    };

    // Önce tam eşleşme ara
    const canonical = unitCanonicalMap[unit.toLowerCase()];
    if (canonical) {
      return t(`inventoryStock.units.${canonical}`);
    }

    // Eğer bulunamazsa orijinal birimi döndür
    return unit;
  };

  const listColumns = [
    {
      field: "stockName",
      headerName: t("inventoryStockMovement.columns.stockName"),
      width: 250,
      valueGetter: (params) => {
        const stock = findStockById(params.row.stock);
        return stock?.name && stock?.stockNumber
          ? `${stock.name} (${stock.stockNumber})`
          : stock?.name || stock?.stockNumber || "-";
      },
      renderCell: (params) => {
        const stock = findStockById(params.row.stock);
        return (
          <div className="stock-info">
            <strong>{stock?.name || "-"}</strong>
            <div className="stock-number">{stock?.stockNumber || "-"}</div>
          </div>
        );
      },
    },
    {
      field: "stockDepo",
      headerName: t("inventoryStockMovement.columns.stockDepo"),
      width: 250,
      valueGetter: (params) => {
        const stock = findStockById(params.row.stock);
        return t(`inventoryStock.depo.${stock?.depo}`) || stock?.depo || "-";
      },
      renderCell: (params) => {
        const stock = findStockById(params.row.stock);
        return t(`inventoryStock.depo.${stock?.depo}`) || stock?.depo || "-";
      },
    },
    {
      field: "date",
      headerName: t("inventoryStockMovement.columns.date"),
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      },
    },
    {
      field: "movementType",
      headerName: t("inventoryStockMovement.columns.movementType"),
      width: 120,
      valueGetter: (params) => getMovementTypeLabel(params.value),
      renderCell: (params) => (
        <span className={`movement-type ${getMovementTypeClass(params.value)}`}>
          {getMovementTypeLabel(params.value)}
        </span>
      ),
    },
    {
      field: "quantity",
      headerName: t("inventoryStockMovement.columns.quantity"),
      width: 150,
      valueGetter: (params) => {
        const stock = findStockById(params.row.stock);
        return `${params.value} ${stock?.unit ? getStockUnitsInTurkish(stock?.unit) : ""}`;
      },
      renderCell: (params) => {
        const stock = findStockById(params.row.stock);
        return (
          <span className="quantity">
            {params.value}
            <span className="unit">{stock?.unit ? getStockUnitsInTurkish(stock?.unit) : ""}</span>
          </span>
        );
      },
    },

    {
      field: "assignee",
      headerName: t("inventoryStockMovement.columns.assignee"),
      width: 250,
      valueGetter: (params) => {
        if (params.row.movementType === "out" && params.row.assigneeType) {
          if (params.row.assigneeType === "other" && params.row.assigneeName) {
            return params.row.assigneeName;
          } else if (params.row.assigneeId && params.row.assigneeType) {
            const assignee = findAssigneeById(params.row.assigneeId, params.row.assigneeType);
            let assigneeName = "";

            if (params.row.assigneeType === "attendant") {
              assigneeName = assignee?.name || "-";
            } else if (params.row.assigneeType === "driver") {
              assigneeName = assignee?.name || "-";
            } else if (params.row.assigneeType === "vehicle") {
              assigneeName = assignee?.plate || "-";
              // Add kilometer info if available
              if (params.row.vehicleKilometer) {
                assigneeName += ` (${params.row.vehicleKilometer} km)`;
              }
            }
            return assigneeName;
          }
        }
        return "-";
      },
      renderCell: (params) => {
        if (params.row.movementType === "out" && params.row.assigneeType) {
          if (params.row.assigneeType === "other" && params.row.assigneeName) {
            return (
              <div className="assignee-info">
                <strong>{params.row.assigneeName}</strong>
                <div className="assignee-type">{t("inventoryStockMovement.assigneeTypes.other")}</div>
              </div>
            );
          } else if (params.row.assigneeId && params.row.assigneeType) {
            const assignee = findAssigneeById(params.row.assigneeId, params.row.assigneeType);
            let assigneeTypeLabel = "";
            let assigneeName = "";

            if (params.row.assigneeType === "attendant") {
              assigneeTypeLabel = t("inventoryStockMovement.assigneeTypes.attendant");
              assigneeName = assignee?.name || "-";
            } else if (params.row.assigneeType === "driver") {
              assigneeTypeLabel = t("inventoryStockMovement.assigneeTypes.driver");
              assigneeName = assignee?.name || "-";
            } else if (params.row.assigneeType === "vehicle") {
              assigneeTypeLabel = t("inventoryStockMovement.assigneeTypes.vehicle");
              assigneeName = assignee?.plate || "-";
              // Add kilometer info if available
              if (params.row.vehicleKilometer) {
                assigneeName += ` (${params.row.vehicleKilometer} km)`;
              }
            }

            return (
              <div className="assignee-info">
                <strong>{assigneeName}</strong>
                <div className="assignee-type">{assigneeTypeLabel}</div>
              </div>
            );
          }
        }
        return "-";
      },
    },
    {
      field: "thirdParty",
      headerName: t("inventoryStockMovement.columns.thirdParty"),
      width: 200,
      valueGetter: (params) => {
        const partner = findThirdPartyById(params.row.thirdParty);
        return partner?.name || "-";
      },
      renderCell: (params) => {
        const partner = findThirdPartyById(params.row.thirdParty);
        return partner?.name || "-";
      },
    },
    {
      field: "cost",
      headerName: t("inventoryStockMovement.columns.cost"),
      width: 130,
      valueGetter: (params) => formatCost(params.value),
      renderCell: (params) => formatCost(params.value),
    },
    {
      field: "description",
      headerName: t("inventoryStockMovement.columns.description"),
      width: 350,
      valueGetter: (params) => params.value || "-",
      renderCell: (params) => <div className="description">{params.value || "-"}</div>,
    },
    {
      field: "referenceNumber",
      headerName: t("inventoryStockMovement.columns.referenceNumber"),
      width: 250,
      valueGetter: (params) => params.row.referenceNumber || "-",
      renderCell: (params) => params.row.referenceNumber || "-",
    },

    {
      field: "createdBy",
      headerName: t("inventoryStockMovement.columns.createdBy"),
      width: 200,
      valueGetter: (params) => params.row.createdBy || "-",
      renderCell: (params) => params.row.createdBy || "-",
    },
  ];

  return (
    <Layout
      itemType={itemType}
      SelectedItemComponent={StockMovementItem}
      NewItemComponent={StockMovementItem}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      hideCreateNewItem={false}
      InsertItem={InsertStockMovement}
      DeleteItem={DeleteStockMovement}
      UpdateItem={UpdateStockMovement}
      isAlwaysFullScreen={true}
      hideCreateNewItemWhenSelected={true}
    />
  );
};

export default StockMovements;
